/** Oracle identity + multi-row data access editor.
 * Used by admins in Users & Access (pass userId) — hits the admin oracle-profile endpoints. */
import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, Plus, Trash, Plugs, CheckCircle, XCircle } from "@phosphor-icons/react";

const ACCESS_COLS = [
  { key: "business_unit", label: "Business Unit", lookup: "business_units" },
  { key: "ledger",        label: "Ledger",        lookup: "ledgers" },
  { key: "inventory_org", label: "Inventory Org", lookup: "inventory_orgs" },
  { key: "subinventory",  label: "Subinventory",  lookup: "subinventories" },
  { key: "asset_book",    label: "Asset Book",    lookup: "asset_books" },
];

const EMPTY_ROW = { business_unit: "", ledger: "", inventory_org: "", subinventory: "", asset_book: "" };

// Text input with a debounced Oracle-lookup dropdown; free text always allowed.
export const LookupInput = ({ lookup, value, onChange, placeholder = "", testid }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let stale = false;
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(`/erp-agents/lookups/${lookup}`, {
          params: { q: value || "", limit: 12 } });
        if (!stale) setRows(data.rows || []);
      } catch { if (!stale) setRows([]); }
      finally { if (!stale) setLoading(false); }
    }, 250);
    return () => { stale = true; clearTimeout(id); };
  }, [open, value, lookup]);

  useEffect(() => {
    const onDoc = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={boxRef} className="relative">
      <input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full border border-gray-300 px-2.5 py-2 text-xs font-mono focus:outline-none focus:border-[#0020C2] bg-white"
      />
      {open && (loading || rows.length > 0) && (
        <div className="absolute z-30 left-0 right-0 top-full mt-0.5 bg-white border border-gray-300 shadow-lg max-h-44 overflow-y-auto">
          {loading && <div className="px-2.5 py-2 text-[11px] text-gray-400 flex items-center gap-1.5">
            <CircleNotch size={11} className="animate-spin" /> Searching Oracle…</div>}
          {!loading && rows.map((r, i) => (
            <button type="button" key={`${r.value}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(r.value); setOpen(false); }}
              className="block w-full text-left px-2.5 py-1.5 text-xs hover:bg-blue-50 border-b border-gray-100 last:border-0">
              <span className="font-mono">{r.label}</span>
              {r.sub && r.sub !== r.label && <span className="text-gray-400 ml-1.5">· {r.sub}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const IDENTITY_FIELDS = [
  { key: "procurement_bu",   label: "Procurement BU",   lookup: "procurement_bus" },
  { key: "business_unit",    label: "Business Unit",    lookup: "business_units" },
  { key: "legal_employer",   label: "Legal Employer",   lookup: "legal_employers" },
  { key: "ledger_name",      label: "Ledger Name",      lookup: "ledgers" },
  { key: "default_currency", label: "Default Currency", lookup: "currencies" },
  { key: "default_country",  label: "Default Country",  lookup: null },
];

export const OracleIdentityPanel = ({ userId, onSaved }) => {
  const base = `/erp-agents/admin/users/${userId}/oracle-profile`;
  const [p, setP] = useState(null);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState(null); // {ok, text}

  const load = useCallback(async () => {
    const { data } = await api.get(base);
    if (!data.data_access?.length) data.data_access = [{ ...EMPTY_ROW }];
    setP(data);
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const setRow = (i, k, v) => setP((x) => ({
    ...x, data_access: x.data_access.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const addRow = () => setP((x) => ({ ...x, data_access: [...x.data_access, { ...EMPTY_ROW }] }));
  const delRow = (i) => setP((x) => ({
    ...x, data_access: x.data_access.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        oracle_username: p.oracle_username, oracle_base_url: p.oracle_base_url,
        procurement_bu: p.procurement_bu, business_unit: p.business_unit,
        legal_employer: p.legal_employer, ledger_name: p.ledger_name,
        default_currency: p.default_currency, default_country: p.default_country,
        data_access: p.data_access.filter((r) => Object.values(r).some((v) => (v || "").trim())),
      };
      if (password) body.oracle_password = password;
      await api.post(base, body);
      setPassword("");
      toast.success("Oracle profile saved");
      await load();
      onSaved && (await onSaved());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestMsg(null);
    try {
      const { data } = await api.post("/erp-agents/test-connection", {
        oracle_base_url: p.oracle_base_url, oracle_username: p.oracle_username,
        oracle_password: password });
      setTestMsg({ ok: data.ok, text: data.ok
        ? `Connected (HTTP ${data.http_status}, ${data.latency_ms}ms)` : data.message });
    } catch (e) {
      setTestMsg({ ok: false, text: e?.response?.data?.detail || "Test failed" });
    } finally { setTesting(false); }
  };

  if (!p) return <div className="text-xs text-gray-400 py-4">Loading Oracle profile…</div>;

  return (
    <div data-testid="oracle-identity-panel" className="space-y-4">
      {/* Connection */}
      <div>
        <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">
          Oracle Pod URL <span className="font-normal normal-case text-gray-400">(leave blank to use the server default)</span>
        </label>
        <div className="mt-1 flex gap-2">
          <input value={p.oracle_base_url || ""} onChange={(e) => set("oracle_base_url", e.target.value)}
            placeholder={p.effective_base_url || "https://…oraclecloud.com"}
            data-testid="oracle-pod-url"
            className="flex-1 border border-gray-300 px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#0020C2]" />
          <button type="button" onClick={test} disabled={testing}
            data-testid="oracle-test-btn"
            className="border border-[#0020C2] text-[#0020C2] px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-[#0020C2] hover:text-white disabled:opacity-50 inline-flex items-center gap-1.5">
            <Plugs size={12} weight="bold" /> {testing ? "Testing…" : "Test"}
          </button>
        </div>
        {p.effective_base_url && (
          <div className="text-[10px] text-gray-500 mt-1">Currently using: <span className="font-mono font-bold">{p.effective_base_url}</span></div>
        )}
        {testMsg && (
          <div data-testid="oracle-test-result"
            className={`mt-1.5 text-[11px] font-semibold inline-flex items-center gap-1 ${testMsg.ok ? "text-emerald-700" : "text-[#DC2626]"}`}>
            {testMsg.ok ? <CheckCircle size={12} weight="fill" /> : <XCircle size={12} weight="fill" />} {testMsg.text}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">Oracle Username</label>
          <input value={p.oracle_username || ""} onChange={(e) => set("oracle_username", e.target.value)}
            data-testid="oracle-username"
            className="mt-1 w-full border border-gray-300 px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#0020C2]" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">
            Oracle Password <span className="font-normal normal-case text-gray-400">(leave blank to keep)</span>
          </label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder={p.has_password ? "••••••••••" : "Not set"}
            data-testid="oracle-password"
            className="mt-1 w-full border border-gray-300 px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#0020C2]" />
        </div>
      </div>
      {/* Default context */}
      <div className="grid grid-cols-2 gap-3">
        {IDENTITY_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">{f.label}</label>
            <div className="mt-1">
              {f.lookup ? (
                <LookupInput lookup={f.lookup} value={p[f.key]} onChange={(v) => set(f.key, v)}
                  testid={`oracle-${f.key}`} />
              ) : (
                <input value={p[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                  data-testid={`oracle-${f.key}`}
                  className="w-full border border-gray-300 px-2.5 py-2 text-xs font-mono focus:outline-none focus:border-[#0020C2]" />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Data access rows */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">Data Access</div>
            <div className="text-[10px] text-gray-400">Each row is one access combination — like Oracle&apos;s Manage Data Access for Users. Type to search Oracle, or enter values freely.</div>
          </div>
          <button type="button" onClick={addRow} data-testid="oracle-access-add-row"
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#0020C2] border border-[#0020C2] px-2 py-1 hover:bg-[#0020C2] hover:text-white shrink-0">
            <Plus size={10} weight="bold" /> Add row
          </button>
        </div>
        <div className="border border-gray-200 overflow-x-auto">
          <table className="w-full" data-testid="oracle-access-table">
            <thead className="bg-gray-50">
              <tr>
                {ACCESS_COLS.map((c) => (
                  <th key={c.key} className="px-2 py-2 text-left text-[9px] tracking-[0.15em] uppercase font-bold text-gray-500 border-b border-gray-300 min-w-[130px]">{c.label}</th>
                ))}
                <th className="w-8 border-b border-gray-300" />
              </tr>
            </thead>
            <tbody>
              {p.data_access.map((row, i) => (
                <tr key={i} className="border-b border-gray-100" data-testid={`oracle-access-row-${i}`}>
                  {ACCESS_COLS.map((c) => (
                    <td key={c.key} className="px-1.5 py-1.5 align-top">
                      <LookupInput lookup={c.lookup} value={row[c.key]}
                        onChange={(v) => setRow(i, c.key, v)}
                        testid={`oracle-access-${c.key}-${i}`} />
                    </td>
                  ))}
                  <td className="px-1.5 py-1.5 text-center align-middle">
                    <button type="button" onClick={() => delRow(i)}
                      data-testid={`oracle-access-del-${i}`}
                      title="Remove row" className="text-gray-400 hover:text-[#DC2626]">
                      <Trash size={13} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={save} disabled={saving}
          data-testid="oracle-profile-save"
          className="bg-[#0020C2] text-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50">
          {saving ? "Saving…" : "Save Oracle profile"}
        </button>
      </div>
    </div>
  );
};
